import React, { useState } from 'react';
import { keyboards, switches, keycaps } from './dataStore';
import { MultiSelectDropdown, SingleSelectDropdown } from './components/Dropdowns';
import emailjs from '@emailjs/browser';

const keyboardPlates = {
    'kb1': ['Polycarbonate'],
    'kb2': ['Aluminum'],
    'kb3': ['Aluminum'],
    'kb4': ['Aluminum', "Polycarbonate"],
    'kb5': ['Aluminum'],
    'kb6': ['Aluminum'],
    'kb7': ['Aluminum', "POM"],
    'kb8': ['Aluminum', "POM"],
    'kb9': ['POM'],
    'kb10': ['Carbon Fiber', "Polypropylene"],
    'kb11': ['FR4', "POM"],
    'kb12': ['Polypropylene'],
    'kb13': ['Polypropylene'],
    'kb14': ['Aluminum', "FR4", "Polycarbonate"],
    'kb15': ['POM'],
    'kb16': ['Aluminum'],
};

const keyboardColors = {
    'kb7': ['E-White', "PC"],
    'kb8': ['E-White', "PC"],
    'kb12': ['Anodized Gray', "Spray-Coat White"],
    'kb13': ['Anodized Gray', "Spray-Coat White"],
};

const foamOptionsList = [
  {id: 'plate', name: 'Plate Foam'},
  {id: 'case', name: 'Case Foam'},
  {id: 'pe', name: 'PE Foam'}
];

function RequestsPage() {
  const [selectedKeyboard, setSelectedKeyboard] = useState('');
  const [selectedSwitch, setSelectedSwitch] = useState('');
  const [selectedKeycap, setSelectedKeycap] = useState('');
  const [selectedPlate, setSelectedPlate] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedFoam, setSelectedFoam] = useState([]);
  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [otherNotes, setOtherNotes] = useState('');
  const [showStatus, setShowStatus] = useState(false);


  const keyboardList = Object.values(keyboards)
    .filter((kbd, idx, arr) => arr.findIndex(k => k.name === kbd.name) === idx)
    .sort((a, b) => a.name.localeCompare(b.name));
  const switchList = Object.values(switches).sort((a, b) => a.name.localeCompare(b.name));
  const keycapList = Object.values(keycaps).sort((a, b) => a.name.localeCompare(b.name));

  const plateOptions = selectedKeyboard && keyboardPlates[selectedKeyboard] ? keyboardPlates[selectedKeyboard] : [];
  const colorOptions = selectedKeyboard && keyboardColors[selectedKeyboard] ? keyboardColors[selectedKeyboard] : [];
  
  const recievingEmail = 'aeykbds@gmail.com';
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowStatus(true);
    setStatus('Sending...');
    const foamValue = selectedFoam.length === 0 ? 'none' : selectedFoam.map((id) => {
      const found = foamOptionsList.find((f) => f.id === id);
      return found ? found.name : id;
    }).join(', ');
    const templateParams = {
      email: recievingEmail,
      name: username || 'Anonymous',
      keyboard: keyboards[selectedKeyboard].name,
      switch: switches[selectedSwitch].name,
      keycap: keycaps[selectedKeycap].name,
      plate: selectedPlate,
      color: selectedColor,
      foam: foamValue,
      other: otherNotes || 'None'
    };
    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID, 
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID, 
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY 
    )
    .then(() => {
      setStatus('Request submitted!');
      setShowStatus(true);
    }, (error) => {
      setStatus('Failed to send request. Please try again.');
      setShowStatus(true);
      console.error('EmailJS error:', error);
    });
  };

  const handleKeyboardChange = (id) => {
    setSelectedKeyboard(id);
    setSelectedPlate('');
    setSelectedColor('');
  };

  // Hide status if error is showing
  const showError =
    !selectedKeyboard ||
    (plateOptions.length > 0 && !selectedPlate) ||
    (colorOptions.length > 0 && !selectedColor) ||
    (!selectedSwitch && !selectedKeycap);
  React.useEffect(() => {
    if (showError && showStatus) setShowStatus(false);
  }, [showError, showStatus]);

  return (
    <div className='min-h-screen bg-[#111112] py-24 px-4 relative overflow-hidden flex items-center justify-center'>
      {/* Ambient blurred backdrop */}
      <div
        aria-hidden='true'
        className='pointer-events-none select-none fixed inset-0 w-full h-full z-0'
        style={{
          backgroundImage: "url('/pictures/database_bg.JPG')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(12px) brightness(0.5)',
          WebkitFilter: 'blur(12px) brightness(0.5)',
          zIndex: 0,
        }}
      />
      <div className='relative z-10 max-w-4xl flex flex-col items-center mx-auto'>
        <h1 className='text-3xl font-bold text-white mb-6 text-center drop-shadow'>Soundtest Request</h1>
        <p className='text-gray-300 mb-4 text-center text-base'>
          Configure a custom build you would like to see featured in a soundtest/showcase. Select your preferred keyboard, switch, keycap, plate, foams and (if available) color. We read every request and will feature your username in the corresponding video or feel free to leave it anonymous.
        </p>
        <p className='text-gray-300 mb-10 text-center text-base'>
        Please check the soundtest page before submitting a request, as we may have already featured the build you are looking for. Duplicates will still be considered if it's been long enough.
        <br /><br />
        Requests will usually take about a week to be fulfilled.
        </p>
        <form className='flex flex-col max-w-xl w-full gap-6 bg-black/80 border border-gray-800 shadow-xl rounded-lg p-8' onSubmit={handleSubmit}> 
          <label className='text-gray-200 font-semibold px-1 '>
            <input
              type='text'
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder='Enter your username or nickname'
              className='w-full mt-2 px-3 py-2 rounded-lg bg-[#18181b] text-white border border-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-700 text-base placeholder-gray-400 transition'
            />
          </label>
          <SingleSelectDropdown
            label='Keyboard'
            options={keyboardList.map((k) => ({ id: k.id, name: k.name }))}
            selected={selectedKeyboard}
            setSelected={handleKeyboardChange}
            className='max-w-full sm:max-w-full w-full'
            showLabelWithSelected={true}
            allowDeselect={true}
          />
          <SingleSelectDropdown
            label='Switch'
            options={switchList.map((s) => ({ id: s.id, name: s.name }))}
            selected={selectedSwitch}
            setSelected={setSelectedSwitch}
            className='max-w-full sm:max-w-full w-full'
            showLabelWithSelected={true}
            allowDeselect={true}
          />
          <SingleSelectDropdown
            label='Keycap'
            options={keycapList.map((k) => ({ id: k.id, name: k.name }))}
            selected={selectedKeycap}
            setSelected={setSelectedKeycap}
            className='max-w-full sm:max-w-full w-full'
            showLabelWithSelected={true}
            allowDeselect={true}
          />
          {plateOptions.length > 0 && (
            <SingleSelectDropdown
              label='Plate'
              options={plateOptions.map((p) => ({ id: p, name: p }))}
              selected={selectedPlate}
              setSelected={setSelectedPlate}
              className='max-w-full sm:max-w-full w-full'
              showLabelWithSelected={true}
              allowDeselect={true}
            />
          )}
          <MultiSelectDropdown
            label='Foams'
            options={foamOptionsList}
            selected={selectedFoam}
            setSelected={setSelectedFoam}
            className='max-w-full sm:max-w-full w-full'
            enableSearch={false}
            showLabelWithSelected={true}
          />
          {colorOptions.length > 0 && (
            <SingleSelectDropdown
              label='Color'
              options={colorOptions.map((p) => ({ id: p, name: p }))}
              selected={selectedColor}
              setSelected={setSelectedColor}
              className='max-w-full sm:max-w-full w-full'
              showLabelWithSelected={true}
              allowDeselect={true}
            />
          )}
          <label className='text-gray-200 font-semibold px-1 '>
            <input
              type='text'
              value={otherNotes}
              onChange={(e) => setOtherNotes(e.target.value)}
              placeholder='Mounting style/additional notes (optional)'
              className='w-full mt-2 px-3 py-2 rounded-lg bg-[#18181b] text-white border border-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-700 text-base placeholder-gray-400 transition'
            />
          </label>
     
          <button
            type='submit'
            className="px-4 py-2 bg-black/80 text-white rounded-full shadow border border-gray-800 hover:bg-[#121212] hover:border-gray-700 transition"
            disabled={
              !selectedKeyboard ||
              (plateOptions.length > 0 && !selectedPlate) ||
              (colorOptions.length > 0 && !selectedColor) ||
              (!selectedSwitch && !selectedKeycap)
            }
          >
            Submit Request
          </button>
          {showError && (
            <p className='text-red-400 text-center mt-2'>Please choose a keyboard/keycap combination, keyboard/switch combination, or a full build.</p>
          )}
          {showStatus && !showError && status && (
            <p className='text-green-400 text-center mt-2'>{status}</p>
          )}
        </form>
      </div>
    </div>
  );
}

export default RequestsPage;
